import { Component, OnDestroy, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { countryPhoneData } from 'phone';
import { filter, firstValueFrom, Observable, SubscriptionLike } from 'rxjs';

import {
  BillParams,
  BillParamsProps,
} from '../../../../../../data/dietitian/domain/bill/bill-params';
import {
  Dietitian,
  DietitianProps,
} from '../../../../../../data/dietitian/domain/dietitian';
import { DietitianState } from '../../../../../../data/dietitian/domain/dietitian_state_provider';
import {
  BillParamsLogoDeleted,
  BillParamsLogoSaved,
  BillParamsSaved,
  BillParamsSignDeleted,
  BillParamsSignSaved,
} from '../../../../../../data/dietitian/domain/events/bill/bill-params-events';
import { AccountBillParamsFacade } from './account-bill-params.facade';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'msd-account-bill-params',
  templateUrl: './account-bill-params.component.html',
  styleUrls: ['./account-bill-params.component.scss'],
  providers: [AccountBillParamsFacade],
})
export class AccountBillParamsComponent implements OnInit, OnDestroy {
  dietitian: Dietitian | undefined;
  dietitianState$: Observable<DietitianState>;
  billParams: BillParamsProps | undefined;
  countries = countryPhoneData;
  isLoading = false;
  isLogoLoading = false;
  isSignLoading = false;
  isPreviewLoading = false;
  isSaving = false;
  currentTabName = 'main';
  adeliNumber: string | undefined;
  typeRegistrationNumber: string | undefined;
  valueTypeRegistrationNumber: string | undefined;
  RPPS = 'RPPS';
  ADELI = 'ADELI';
  INAMI = 'INAMI';
  OTHER = '_';

  private dietitianSubscription: SubscriptionLike;
  private billParamsEventSubscription: SubscriptionLike;

  constructor(private facade: AccountBillParamsFacade, toastr: ToastrService) {
    this.dietitianState$ = this.facade.dietitianState$;
    this.dietitianSubscription = this.dietitianState$.subscribe(
      async (state) => {
        if (state.origin !== 'AccountBillParamsFacade.saveDietitian') {
          this.isLoading = true;
          this.dietitian = state.entity;
          if (this.dietitian) {
            this.billParams = (
              await this.facade.load(this.dietitian.dietitianId.id.toString())
            ).props;
            this.adeliNumber = this.dietitian?.adeliNumber;
            this.typeRegistrationNumber = this.dietitian?.typeAdeliNumber;
            this.valueTypeRegistrationNumber =
              this.dietitian?.typeValueAdeliNumber;
            this.isLoading = false;
            this.isPreviewLoading = true;
            this.billParams.previewUrl = await this.facade.previewPdf();
            this.isPreviewLoading = false;
          }
        } else {
          this.dietitian = state.entity;
          if (this.dietitian) {
            this.adeliNumber = this.dietitian.adeliNumber;
            this.typeRegistrationNumber = this.dietitian.typeAdeliNumber;
            this.valueTypeRegistrationNumber =
              this.dietitian.typeValueAdeliNumber;
            this.billParams = (
              await this.facade.load(this.dietitian.dietitianId.id.toString())
            ).props;
          }
        }
      },
    );

    this.billParamsEventSubscription =
      this.facade.billParamsEventProvider$.subscribe((event) => {
        if (event instanceof BillParamsSaved) {
          toastr.success('Paramètres Facturation enregistrés');
        }
        if (event instanceof BillParamsLogoSaved) {
          toastr.success('Paramètres Facturation - Logo enregistré');
        }
        if (event instanceof BillParamsLogoDeleted) {
          toastr.success('Paramètres Facturation - Logo supprimé');
        }
        if (event instanceof BillParamsSignSaved) {
          toastr.success('Paramètres Facturation - Signature enregistrée');
        }
        if (event instanceof BillParamsSignDeleted) {
          toastr.success('Paramètres Facturation - Signature supprimée');
        }
      });
  }

  ngOnInit(): void {
    firstValueFrom(
      this.dietitianState$.pipe(filter((state) => state.entity != null)),
    ).then((state) => {
      this.dietitian = state.entity;
    });
  }

  ngOnDestroy(): void {
    this.dietitianSubscription?.unsubscribe();
    this.billParamsEventSubscription?.unsubscribe();
  }

  get taxNumberInvalid(): boolean {
    return false;
  }

  get taxRateInvalid(): boolean {
    if (this.billParams && this.billParams.tax === 'with') {
      return !this.billParams.taxRate || this.billParams.taxRate === 0;
    }
    return false;
  }

  async logoSelected(event: any): Promise<void> {
    if (event && event.target && event.target.files && this.billParams) {
      this.isLogoLoading = true;
      if (!event.target.files?.length) {
        return undefined;
      }
      const file = event?.target?.files[0];
      event.target.value = '';
      this.billParams = (
        await this.facade.updateLogo(file, BillParams.create(this.billParams))
      ).props;
      this.isLogoLoading = false;

      if (this.billParams) {
        this.isPreviewLoading = true;
        this.billParams.previewUrl = await this.facade.previewPdf();
        this.isPreviewLoading = false;
      }
    }
  }

  async signSelected(event: any): Promise<void> {
    if (event && event.target && event.target.files && this.billParams) {
      this.isSignLoading = true;
      if (!event.target.files?.length) {
        return undefined;
      }
      const file = event?.target?.files[0];
      event.target.value = '';
      this.billParams = (
        await this.facade.updateSign(file, BillParams.create(this.billParams))
      ).props;
      this.isSignLoading = false;

      if (this.billParams) {
        this.isPreviewLoading = true;
        this.billParams.previewUrl = await this.facade.previewPdf();
        this.isPreviewLoading = false;
      }
    }
  }

  async deleteLogoPicture(): Promise<void> {
    if (this.billParams) {
      this.isLogoLoading = true;
      this.billParams = (await this.facade.deleteLogo(this.billParams)).props;
      this.isLogoLoading = false;
      this.isPreviewLoading = true;
      this.billParams.previewUrl = await this.facade.previewPdf();
      this.isPreviewLoading = false;
    }
  }

  async deleteSignPicture(): Promise<void> {
    if (this.billParams) {
      this.isSignLoading = true;
      this.billParams = (await this.facade.deleteSign(this.billParams)).props;
      this.isSignLoading = false;
      this.isPreviewLoading = true;
      this.billParams.previewUrl = await this.facade.previewPdf();
      this.isPreviewLoading = false;
    }
  }

  get withTax(): boolean {
    return this.billParams?.tax === 'with';
  }

  onChangeBillTax(value: string) {
    if (this.billParams) {
      this.billParams.noTaxMention = value === 'without';
    }
  }

  isActive(tabName: string): boolean {
    return this.currentTabName === tabName;
  }

  onTab(tabName: string) {
    this.currentTabName = tabName;
  }

  async saveBillParams() {
    if (!this.isSaving) {
      this.isSaving = true;
      if (this.billParams) {
        if (
          this.dietitian &&
          (this.dietitian.adeliNumber !== this.adeliNumber ||
            this.dietitian.typeValueAdeliNumber !==
              this.valueTypeRegistrationNumber ||
            this.dietitian.typeAdeliNumber !== this.typeRegistrationNumber)
        ) {
          const dietitianWithNewAdeli = this.dietitian.copyWith({
            adeliNumber: this.adeliNumber,
            typeAdeliNumber: this.adeliNumber
              ? this.typeRegistrationNumber
              : undefined,
            typeValueAdeliNumber: this.adeliNumber
              ? this.valueTypeRegistrationNumber
              : undefined,
          } as DietitianProps);
          await this.facade.saveDietitian(dietitianWithNewAdeli);
        }

        this.billParams = (
          await this.facade.save(BillParams.create(this.billParams))
        ).props;
      }
      this.isSaving = false;

      if (this.billParams) {
        this.isPreviewLoading = true;
        this.billParams.previewUrl = await this.facade.previewPdf();
        this.isPreviewLoading = false;
      }
    }
  }

  openPreview() {
    window.open(this.billParams?.previewUrl, '_blank');
  }

  onChangeTypeRegistrationNumber(type: string) {
    if (type === 'OTHER') {
      this.valueTypeRegistrationNumber;
    }
  }
}
